import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MessageCircle, Send, RefreshCw, Pause, Play, Hand, Bot, PauseCircle, ArrowLeft, PanelRight, Power, PowerOff, Info, RotateCcw } from 'lucide-react';
import { chatsService } from '../../../services/api';
import usePolling from '../../../hooks/usePolling';
import Modal from '../PuntoV/Modal';
import Boton from './Boton';
import MensajeMedia, { esNoTexto } from './MensajeMedia';

const INTERVALO_LISTA_MS = 15 * 1000;
const INTERVALO_CHAT_MS = 5 * 1000;

const fmtHora = (ms) => new Date(ms).toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit' });
const fmtDia = (ms) => new Date(ms).toLocaleDateString('es-VE', { day: '2-digit', month: 'short' });

// Hora relativa corta: minutos y horas hoy, "ayer", y luego la fecha.
const fmtRelativa = (ms) => {
  const min = Math.floor((Date.now() - ms) / 60000);
  if (min < 1) return 'ahora';
  if (min < 60) return `hace ${min} min`;
  const hoy = new Date().toDateString();
  if (new Date(ms).toDateString() === hoy) return `hace ${Math.floor(min / 60)} h`;
  if (new Date(ms).toDateString() === new Date(Date.now() - 86400000).toDateString()) return 'ayer';
  return fmtDia(ms);
};

// Escala cualitativa sobre los rangos del bot: hasta 30 curioso, 31 a 60 interesado, desde 61 cerca de comprar.
const nivelScore = (score) => {
  if (score === null || score === undefined) return { texto: 'Sin evaluar', clase: 'bg-gray-100 text-gray-600' };
  if (score >= 61) return { texto: 'Caliente', clase: 'bg-orange-100 text-orange-800' };
  if (score >= 31) return { texto: 'Tibio', clase: 'bg-amber-100 text-amber-800' };
  return { texto: 'Frío', clase: 'bg-sky-100 text-sky-800' };
};

const InsigniaScore = ({ score }) => {
  const { texto, clase } = nivelScore(score);
  const titulo = score === null || score === undefined ? 'El bot aún no evalúa este lead' : `Interés del lead según el bot (${score}/100)`;
  return (
    <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap ${clase}`} title={titulo}>
      {texto}
    </span>
  );
};

// Un chat espera a una persona cuando el bot esta en pausa y n8n informo un handoff.
// Un chat sin habilitar nunca es pendiente: el bot lo ignora.
const esPendiente = (c) => c.botHabilitado !== false && c.botPausado === true && !!c.handoff;

// Estado unico del bot en un chat. botHabilitado null (n8n sin desplegar) se trata como habilitado.
const estadoBot = (c) => {
  if (c.botHabilitado === false) return 'sin_habilitar';
  if (esPendiente(c)) return 'pendiente';
  if (c.botPausado === true) return 'pausa';
  if (c.botPausado === false) return 'activo';
  return 'desconocido';
};

const ETIQUETA_ESTADO = {
  sin_habilitar: { texto: 'Bot sin habilitar', clase: 'bg-gray-100 text-gray-700', ayuda: 'El bot no responde en este chat hasta que lo habilites' },
  pendiente: { texto: 'Pendiente', clase: 'bg-red-100 text-red-800', ayuda: 'Necesita a una persona' },
  pausa: { texto: 'En pausa', clase: 'bg-amber-100 text-amber-800', ayuda: 'El bot no responde: atiende una persona' },
  activo: { texto: 'Bot activo', clase: 'bg-green-100 text-green-800', ayuda: 'El bot responde los mensajes nuevos del cliente' },
  desconocido: { texto: 'Estado no disponible', clase: 'bg-gray-100 text-gray-600', ayuda: 'No se pudo leer el estado del bot' }
};

const InsigniaEstado = ({ chat }) => {
  const { texto, clase, ayuda } = ETIQUETA_ESTADO[estadoBot(chat)];
  return <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap ${clase}`} title={ayuda}>{texto}</span>;
};

// Pendientes primero (el que lleva mas tiempo esperando arriba), luego por actividad reciente.
const ordenarChats = (lista) => [...lista].sort((a, b) => {
  const pa = esPendiente(a);
  const pb = esPendiente(b);
  if (pa !== pb) return pa ? -1 : 1;
  if (pa) return new Date(a.handoff.desde || 0) - new Date(b.handoff.desde || 0);
  return b.ultimaActividad - a.ultimaActividad;
});

const ETIQUETAS_MEDIO = { ptt: 'Nota de voz', audio: 'Audio', image: 'Imagen', video: 'Video', document: 'Documento', sticker: 'Sticker', call_log: 'Llamada' };

const textoVistaPrevia = (m) => {
  const medio = /^\[(\w+)\]/.exec(m.texto || '');
  return medio ? ETIQUETAS_MEDIO[medio[1]] || m.texto : m.texto;
};

const iniciales = (nombre) => (nombre || '?').trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase();

const IconoEstado = ({ chat }) => {
  const estado = estadoBot(chat);
  const etiqueta = ETIQUETA_ESTADO[estado].ayuda;
  if (estado === 'pendiente') return <Hand size={16} className="text-red-600 shrink-0" aria-label={etiqueta} />;
  if (estado === 'pausa') return <PauseCircle size={16} className="text-amber-600 shrink-0" aria-label={etiqueta} />;
  if (estado === 'activo') return <Bot size={16} className="text-green-600 shrink-0" aria-label={etiqueta} />;
  if (estado === 'sin_habilitar') return <PowerOff size={16} className="text-gray-400 shrink-0" aria-label={etiqueta} />;
  return null;
};

const fmtDesde = (iso) => (iso ? fmtRelativa(new Date(iso).getTime()).replace('hace ', '') : null);

const ChatsView = () => {
  const [chats, setChats] = useState([]);
  const [seleccionado, setSeleccionado] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [cambiandoBot, setCambiandoBot] = useState(false);
  const [dialogoReanudar, setDialogoReanudar] = useState(false);
  const [dialogoHabilitar, setDialogoHabilitar] = useState(null);
  const [responderPendientes, setResponderPendientes] = useState(true);
  const [reiniciarConversacion, setReiniciarConversacion] = useState(true);
  const [mensajeAntes, setMensajeAntes] = useState('');
  const [aviso, setAviso] = useState('');
  const [error, setError] = useState('');
  const [filtro, setFiltro] = useState('todos');
  const [panelAbierto, setPanelAbierto] = useState(true);
  const fondoRef = useRef(null);

  const lista = usePolling(async () => {
    const res = await chatsService.listar();
    setChats(res.data || []);
    setError('');
  }, INTERVALO_LISTA_MS);

  const conversacion = usePolling(async () => {
    if (!seleccionado) return;
    const res = await chatsService.mensajes(seleccionado);
    setMensajes(res.data || []);
  }, INTERVALO_CHAT_MS);

  useEffect(() => {
    setMensajes([]);
    conversacion.refrescar();
    // refrescar cambia de identidad solo si cambia el intervalo, que es fijo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seleccionado]);

  useEffect(() => {
    fondoRef.current?.scrollIntoView({ block: 'end' });
  }, [mensajes]);

  useEffect(() => {
    if (lista.fallos > 0 && chats.length === 0) {
      chatsService.listar().catch((e) => setError(e.message || 'No se pudo cargar los chats'));
    }
  }, [lista.fallos, chats.length]);

  const fijarPausa = (id, botPausado) => setChats((cs) => cs.map((c) => (c.id === id ? { ...c, botPausado } : c)));

  // Mensajes del cliente que nadie ha contestado: los de texto escritos despues del ultimo saliente.
  const pendientes = [];
  for (let i = mensajes.length - 1; i >= 0 && !mensajes[i].saliente; i--) {
    if (mensajes[i].tipo === 'chat') pendientes.unshift(mensajes[i].texto);
  }

  const cambiarBot = async (pausado, opciones) => {
    setCambiandoBot(true);
    setAviso('');
    try {
      const res = await chatsService.pausarBot(seleccionado, pausado, opciones);
      fijarPausa(seleccionado, res.data.botPausado);
      setError('');
      if (res.data.aviso) setError(res.data.aviso);
      else if (res.data.pendientesRespondidos) setAviso(`Bot reanudado: está respondiendo ${res.data.pendientesRespondidos} mensaje(s) pendiente(s). La respuesta llega en unos segundos.`);
      setDialogoReanudar(false);
      lista.refrescar();
      conversacion.refrescar();
    } catch (e) {
      setError(e.message || 'No se pudo cambiar el estado del bot');
    } finally {
      setCambiandoBot(false);
    }
  };

  const cambiarHabilitacion = async (habilitado) => {
    setCambiandoBot(true);
    setAviso('');
    try {
      const res = await chatsService.habilitarBot(seleccionado, habilitado);
      setChats((cs) => cs.map((c) => (c.id === seleccionado ? { ...c, botHabilitado: res.data.botHabilitado, ...(res.data.botHabilitado === false ? { handoff: null } : {}) } : c)));
      setError('');
      setAviso(res.data.botHabilitado
        ? 'Bot habilitado en este chat: desde ahora contestará los mensajes nuevos del cliente.'
        : 'Bot deshabilitado en este chat: ya no responde ni guarda historial.');
      setDialogoHabilitar(null);
      lista.refrescar();
    } catch (e) {
      setError(e.message || 'No se pudo cambiar la habilitación del bot');
    } finally {
      setCambiandoBot(false);
    }
  };

  const alternarBot = () => {
    const chatActual = chats.find((c) => c.id === seleccionado);
    if (!chatActual || cambiandoBot) return;
    if (!chatActual.botPausado) {
      cambiarBot(true);
      return;
    }
    setResponderPendientes(pendientes.length > 0);
    setReiniciarConversacion(true);
    setMensajeAntes('');
    setDialogoReanudar(true);
  };

  const confirmarReanudar = () => cambiarBot(false, {
    responderPendientes: responderPendientes && pendientes.length > 0,
    reiniciarConversacion,
    ...(mensajeAntes.trim() ? { mensaje: mensajeAntes.trim() } : {})
  });

  const enviar = async () => {
    const mensaje = texto.trim();
    if (!mensaje || !seleccionado || enviando) return;
    setEnviando(true);
    try {
      const res = await chatsService.enviar(seleccionado, mensaje);
      // El backend pausa el bot al enviar un asesor; si n8n no respondio, botPausado llega null.
      // n8n borra el handoff al pausar: aqui se refleja sin esperar al siguiente listado.
      if (res.data?.botPausado === true) setChats((cs) => cs.map((c) => (c.id === seleccionado ? { ...c, botPausado: true, handoff: null } : c)));
      setTexto('');
      setError('');
      await conversacion.refrescar();
    } catch (e) {
      setError(e.message || 'No se pudo enviar el mensaje');
    } finally {
      setEnviando(false);
    }
  };

  const alTeclear = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviar();
    }
  };

  const chat = chats.find((c) => c.id === seleccionado);
  const totalPendientes = chats.filter(esPendiente).length;
  const totalConBot = chats.filter((c) => c.botHabilitado === true).length;
  const visibles = useMemo(
    () => ordenarChats(filtro === 'pendientes' ? chats.filter(esPendiente) : filtro === 'con_bot' ? chats.filter((c) => c.botHabilitado === true) : chats),
    [chats, filtro]
  );
  const foco = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';

  return (
    <div className="h-full flex flex-col bg-white">
      <div className="flex items-center gap-3 border-b border-gray-200 pl-16 pr-4 lg:pl-4 py-3">
        <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <MessageCircle size={20} aria-hidden="true" /> Chats
        </h1>
        {totalPendientes > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-red-600 px-2.5 py-0.5 text-xs font-semibold text-white" role="status">
            <Hand size={13} aria-hidden="true" /> {totalPendientes} {totalPendientes === 1 ? 'espera' : 'esperan'} a una persona
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          <Boton variante="icono" icono={RefreshCw} etiqueta="Actualizar" onClick={() => { lista.refrescar(); conversacion.refrescar(); }} />
          <Boton
            variante="icono"
            icono={PanelRight}
            etiqueta={panelAbierto ? 'Ocultar datos del lead' : 'Mostrar datos del lead'}
            onClick={() => setPanelAbierto((v) => !v)}
            aria-pressed={panelAbierto}
            className={`hidden xl:inline-flex ${panelAbierto ? '!text-blue-600' : ''}`}
          />
        </div>
      </div>

      {error && <div className="mx-4 mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      {aviso && <div className="mx-4 mt-3 rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">{aviso}</div>}

      <div className={`flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[360px_1fr] ${panelAbierto ? 'xl:grid-cols-[360px_1fr_300px]' : ''}`}>
        <div className={`${seleccionado ? 'hidden md:flex' : 'flex'} flex-col min-h-0 border-r border-gray-200`}>
          <div className="flex gap-1 border-b border-gray-200 p-2" role="group" aria-label="Filtrar chats">
            {[['todos', 'Todos'], ['con_bot', `Con bot (${totalConBot})`], ['pendientes', `Pendientes (${totalPendientes})`]].map(([id, texto]) => (
              <button
                key={id}
                type="button"
                onClick={() => setFiltro(id)}
                aria-pressed={filtro === id}
                className={`min-h-[32px] rounded-md px-3 py-1 text-xs font-medium ${filtro === id ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-100'} ${foco}`}
              >
                {texto}
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto">
            {visibles.length === 0 && (
              <p className="p-4 text-sm text-gray-500">{filtro === 'pendientes' ? 'Ningún chat espera a una persona.' : filtro === 'con_bot' ? 'El bot no está habilitado en ningún chat.' : 'No hay chats todavía.'}</p>
            )}
            {visibles.map((c) => {
              const pendiente = esPendiente(c);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setSeleccionado(c.id)}
                  aria-current={c.id === seleccionado ? 'true' : undefined}
                  className={`w-full text-left px-3 py-3 border-b border-gray-100 border-l-4 flex items-start gap-3 ${foco} focus-visible:ring-inset ${
                    pendiente ? 'border-l-red-600 bg-red-50 hover:bg-red-100' : 'border-l-transparent hover:bg-gray-50'
                  } ${c.id === seleccionado ? (pendiente ? 'bg-red-100' : 'bg-blue-50') : ''}`}
                >
                  <span className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${pendiente ? 'bg-red-600 text-white' : 'bg-slate-200 text-slate-700'}`} aria-hidden="true">
                    {iniciales(c.nombre)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5">
                      <IconoEstado chat={c} />
                      <span className="truncate text-sm font-semibold text-gray-900">{c.nombre}</span>
                      <span className="ml-auto shrink-0 text-xs text-gray-500">{fmtRelativa(c.ultimaActividad)}</span>
                    </span>
                    {pendiente && <span className="block truncate text-xs font-semibold text-red-700">{c.handoff.motivo}</span>}
                    <span className="mt-0.5 block truncate text-xs text-gray-600">
                      {c.ultimoMensaje
                        ? <><span className="font-medium">{c.ultimoMensaje.saliente ? 'Tú: ' : 'Cliente: '}</span>{textoVistaPrevia(c.ultimoMensaje)}</>
                        : 'Sin mensajes'}
                    </span>
                    <span className="mt-1 flex items-center gap-1.5">
                      <InsigniaScore score={c.scoreLead} />
                      <InsigniaEstado chat={c} />
                      {c.noLeidos > 0 && <span className="ml-auto rounded-full bg-green-600 px-2 py-0.5 text-xs text-white" aria-label={`${c.noLeidos} sin leer`}>{c.noLeidos}</span>}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className={`${seleccionado ? 'flex' : 'hidden md:flex'} flex-col min-h-0 min-w-0`}>
          {!seleccionado ? (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-500">Elige un chat para ver la conversación.</div>
          ) : (
            <>
              <div className="px-3 py-3 border-b border-gray-200 flex items-center gap-3 flex-wrap">
                <button type="button" onClick={() => setSeleccionado(null)} className={`md:hidden rounded p-1.5 text-gray-600 ${foco}`} aria-label="Volver a la lista de chats">
                  <ArrowLeft size={20} />
                </button>
                <span className="text-sm font-semibold text-gray-900">{chat?.nombre || seleccionado}</span>
                {chat && <InsigniaScore score={chat.scoreLead} />}
                {chat && <InsigniaEstado chat={chat} />}
                {chat && chat.botHabilitado === false && (
                  <Boton variante="primario" icono={Power} className="ml-auto" disabled={cambiandoBot} onClick={() => setDialogoHabilitar('habilitar')}>Habilitar bot en este chat</Boton>
                )}
                {chat && chat.botHabilitado !== false && (
                  <div className="ml-auto flex items-center gap-2">
                    {chat.botPausado !== null && chat.botPausado !== undefined && (
                      <Boton icono={chat.botPausado ? Play : Pause} cargando={cambiandoBot} onClick={alternarBot} etiqueta={chat.botPausado ? 'Reanudar el bot en este chat' : 'Pausar el bot: atiende una persona'}>
                        {chat.botPausado ? 'Reanudar bot' : 'Pausar bot'}
                      </Boton>
                    )}
                    {chat.botHabilitado === true && (
                      <Boton variante="peligro" icono={PowerOff} disabled={cambiandoBot} onClick={() => setDialogoHabilitar('deshabilitar')} etiqueta="El bot dejará de responder en este chat">Deshabilitar</Boton>
                    )}
                  </div>
                )}
              </div>
              {chat && esPendiente(chat) && (
                <div className="flex items-start gap-2 border-b border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800" role="alert">
                  <Hand size={18} className="mt-0.5 shrink-0" aria-hidden="true" />
                  <span>
                    <strong>Necesita a una persona{fmtDesde(chat.handoff.desde) ? ` desde hace ${fmtDesde(chat.handoff.desde)}` : ''}:</strong> {chat.handoff.motivo}
                  </span>
                </div>
              )}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 bg-gray-50">
                {mensajes.map((m) => (
                  <div key={m.id} className={`flex ${m.saliente ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] md:max-w-[75%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words ${m.saliente ? 'bg-green-100 text-gray-900' : 'bg-white border border-gray-200 text-gray-900'}`}>
                      {esNoTexto(m) ? <MensajeMedia chatId={seleccionado} m={m} /> : m.texto}
                      {!m.saliente && esNoTexto(m) && (
                        <p className="mt-1 flex items-center gap-1 text-[11px] text-gray-500"><Info size={12} aria-hidden="true" /> El bot no lee audios, fotos ni archivos</p>
                      )}
                      <div className="mt-1 text-[10px] text-gray-600 text-right">{fmtHora(m.fecha)}</div>
                    </div>
                  </div>
                ))}
                <div ref={fondoRef} />
              </div>
              <div className="p-3 border-t border-gray-200 flex items-end gap-2">
                <textarea
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  onKeyDown={alTeclear}
                  rows={2}
                  maxLength={4096}
                  aria-label="Mensaje"
                  placeholder="Escribe un mensaje (Enter envía, Shift+Enter salto de línea)"
                  className="flex-1 resize-none rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <Boton variante="primario" icono={Send} cargando={enviando} disabled={!texto.trim()} onClick={enviar} className="min-h-[44px] px-4">Enviar</Boton>
              </div>
            </>
          )}
        </div>

        {panelAbierto && seleccionado && chat && (
          <aside className="hidden xl:block border-l border-gray-200 overflow-y-auto p-4 text-sm" aria-label="Datos del lead">
            <h2 className="mb-3 text-xs font-bold uppercase tracking-wide text-gray-500">Lead</h2>
            <p className="font-semibold text-gray-900">{chat.nombre}</p>
            <dl className="mt-3 space-y-3">
              <div>
                <dt className="text-xs text-gray-500">Interés</dt>
                <dd className="mt-0.5 flex items-center gap-2"><InsigniaScore score={chat.scoreLead} />{chat.scoreLead !== null && chat.scoreLead !== undefined && <span className="text-xs text-gray-500">{chat.scoreLead}/100</span>}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Bot</dt>
                <dd className="mt-0.5"><InsigniaEstado chat={chat} /></dd>
              </div>
              {esPendiente(chat) && (
                <div>
                  <dt className="text-xs text-gray-500">Motivo del handoff</dt>
                  <dd className="mt-0.5 font-medium text-red-700">{chat.handoff.motivo}</dd>
                  {chat.handoff.desde && <dd className="text-xs text-gray-500">Desde {fmtDia(new Date(chat.handoff.desde).getTime())} {fmtHora(new Date(chat.handoff.desde).getTime())}</dd>}
                  {chat.handoff.ultimoMensaje && <dd className="mt-1 rounded bg-gray-50 border border-gray-200 px-2 py-1 text-gray-800 break-words">{chat.handoff.ultimoMensaje}</dd>}
                </div>
              )}
              <div>
                <dt className="text-xs text-gray-500">Última actividad</dt>
                <dd className="mt-0.5 text-gray-900">{fmtDia(chat.ultimaActividad)} {fmtHora(chat.ultimaActividad)}</dd>
              </div>
            </dl>
          </aside>
        )}
      </div>

      {dialogoReanudar && (
        <Modal
          titulo="Reanudar el bot"
          subtitulo={chat?.nombre || seleccionado}
          icono={Play}
          ancho="md"
          bloqueado={cambiandoBot}
          onClose={() => setDialogoReanudar(false)}
          pie={(
            <>
              <Boton onClick={() => setDialogoReanudar(false)} disabled={cambiandoBot}>Cancelar</Boton>
              <Boton variante="primario" icono={Play} cargando={cambiandoBot} onClick={confirmarReanudar}>{cambiandoBot ? 'Reanudando...' : 'Reanudar bot'}</Boton>
            </>
          )}
        >
          {pendientes.length > 0 ? (
            <div className="mb-4">
              <p className="text-sm text-gray-700 mb-2">El cliente escribió esto y nadie le ha respondido:</p>
              <ul className="rounded border border-gray-200 bg-gray-50 divide-y divide-gray-200 text-sm text-gray-800">
                {pendientes.map((texto, i) => <li key={i} className="px-3 py-2 whitespace-pre-wrap break-words">{texto}</li>)}
              </ul>
              <label className="mt-3 flex items-start gap-2 text-sm text-gray-800">
                <input type="checkbox" className="mt-0.5" checked={responderPendientes} onChange={(e) => setResponderPendientes(e.target.checked)} />
                Que el bot responda ahora a estos mensajes
              </label>
            </div>
          ) : (
            <p className="mb-4 text-sm text-gray-600">No hay mensajes del cliente sin responder: el bot contestará cuando escriba de nuevo.</p>
          )}
          <label className="mb-4 flex items-start gap-2 text-sm text-gray-800">
            <input type="checkbox" className="mt-0.5" checked={reiniciarConversacion} onChange={(e) => setReiniciarConversacion(e.target.checked)} />
            <span>
              <span className="inline-flex items-center gap-1"><RotateCcw size={13} aria-hidden="true" /> Empezar la conversación de cero</span>
              <span className="block text-xs text-gray-500">El bot olvida lo hablado antes (historial y score) y responde sin ese contexto.</span>
            </span>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-600 mb-1">Enviar un mensaje antes de reanudar (opcional)</span>
            <textarea
              rows={2}
              maxLength={4096}
              value={mensajeAntes}
              onChange={(e) => setMensajeAntes(e.target.value)}
              placeholder="Ej. Gracias por esperar, te sigue atendiendo nuestro asistente."
              className="w-full resize-none rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </label>
        </Modal>
      )}

      {dialogoHabilitar && (
        <Modal
          titulo={dialogoHabilitar === 'habilitar' ? 'Habilitar el bot en este chat' : 'Deshabilitar el bot en este chat'}
          subtitulo={chat?.nombre || seleccionado}
          icono={dialogoHabilitar === 'habilitar' ? Power : PowerOff}
          tono={dialogoHabilitar === 'habilitar' ? 'verde' : 'rojo'}
          ancho="md"
          bloqueado={cambiandoBot}
          onClose={() => setDialogoHabilitar(null)}
          pie={(
            <>
              <Boton onClick={() => setDialogoHabilitar(null)} disabled={cambiandoBot}>Cancelar</Boton>
              {dialogoHabilitar === 'habilitar'
                ? <Boton variante="primario" icono={Power} cargando={cambiandoBot} onClick={() => cambiarHabilitacion(true)}>Habilitar bot</Boton>
                : <Boton variante="peligro" icono={PowerOff} cargando={cambiandoBot} onClick={() => cambiarHabilitacion(false)}>Deshabilitar bot</Boton>}
            </>
          )}
        >
          <p className="text-sm text-gray-700">
            {dialogoHabilitar === 'habilitar'
              ? 'Desde este momento el bot contestará los mensajes nuevos del cliente en este chat. Lo que escribió antes no se responde.'
              : 'El bot dejará de responder en este chat y no guardará su historial. Puedes volver a habilitarlo cuando quieras.'}
          </p>
        </Modal>
      )}
    </div>
  );
};

export default ChatsView;
